<template>
    <div class="accordion" :class="accordionClasses">
        <template v-for="(item, index) in items">
            <slot :name="'before-item-' + (slotKey ? item[slotKey] : index)" />
            <div
                :key="item.id"
                class="accordion__item"
                :class="{
                    'accordion__item--hidden': item.hidden,
                    [`accordion__item--${item.status}`]: item.status,
                    'accordion__item--freeze-open': item.freezeOpen,
                }"
            >
                <div
                    class="accordion__header"
                    :class="firstChildHeaderClass"
                    :aria-expanded="isOpen"
                    @click="toggle(index)"
                >
                    <div class="accordion__title-wrapper">
                        <svg-icon v-if="item.icon" :name="item.icon" />
                        <svg-icon v-else-if="STATUS_ICONS[item.status]" :name="STATUS_ICONS[item.status]" />
                        <div class="accordion__title-text">{{ item.title }}</div>
                        <slot :name="'header-desc-' + (slotKey ? item[slotKey] : index)" :item="item" />
                    </div>
                    <svg-icon
                        :name="defaultArrowDirection === 'down' ? 'keyboard_arrow_down' : 'keyboard_arrow_right'"
                        :style="{ '--arrow-rotation': defaultArrowDirection === 'right' ? '90deg' : '180deg' }"
                        :class="{ 'accordion__icon--rotated': isOpen[index] }"
                    />
                </div>
                <div
                    v-show="(item.freezeOpen || isOpen[index]) && $slots['item-' + (slotKey ? item[slotKey] : index)]"
                    class="accordion__content"
                >
                    <slot :name="'item-' + (slotKey ? item[slotKey] : index)" :item="item">
                        <div v-if="item.content">{{ item.content }}</div>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div v-if="item.body" v-html="item.body" />
                    </slot>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
const WHITE = 'white';
const CREAM = 'cream';
const SPACING = {
    none: 'none',
    small: 'small',
    medium: 'medium',
    large: 'large',
};

const STATUS_ICONS = {
    success: 'check_circle',
    error: 'close_circle_outline',
    info: 'info-outline',
};

const MINIMALISTIC = 'minimalistic';

export default {
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        mode: {
            type: String,
            default: '',
            validator: value => ['', MINIMALISTIC].includes(value),
        },
        backgroundColor: {
            type: String,
            default: WHITE,
            validator: value => [WHITE, CREAM].includes(value),
        },
        allowMultipleOpen: {
            type: Boolean,
            default: true,
        },
        defaultArrowDirection: {
            type: String,
            default: 'down',
            validator: value => ['right', 'down'].includes(value),
        },
        /**
         * Mainly used for CMS content
         */
        bottomSpacing: {
            type: String,
            default: SPACING.none,
            validator: value => Object.values(SPACING).includes(value),
        },
        slotKey: {
            type: String,
            default: '',
        },
    },
    data() {
        const isOpen = this.items.map(item => item.defaultOpen || false);

        return {
            isOpen,
            MINIMALISTIC,
            STATUS_ICONS,
        };
    },
    computed: {
        accordionClasses() {
            return {
                'accordion--cream': this.backgroundColor === CREAM,
                'accordion--bottom-spacing-small': this.bottomSpacing === SPACING.small,
                'accordion--bottom-spacing-medium': this.bottomSpacing === SPACING.medium,
                'accordion--bottom-spacing-large': this.bottomSpacing === SPACING.large,
                'accordion--minimalistic': this.mode === MINIMALISTIC,
            };
        },
        firstChildHeaderClass() {
            return {
                'accordion__header--with-top-padding': this.backgroundColor !== WHITE,
            };
        },
    },
    methods: {
        toggle(index) {
            if (!this.allowMultipleOpen) {
                this.isOpen = this.isOpen.map((open, i) => {
                    const isCurrentItem = i === index;

                    if (isCurrentItem && !open) {
                        this.$emit('item-opened', index);
                    }

                    return isCurrentItem && !open;
                });
            } else {
                this.$set(this.isOpen, index, !this.isOpen[index]);

                if (!this.isOpen[index]) {
                    this.$emit('item-opened', index);
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

.accordion {
    position: relative;
    padding: 0 24px;

    &__item {
        -webkit-tap-highlight-color: transparent;

        &:first-child {
            .accordion__header {
                padding-top: 0px;

                &--with-top-padding {
                    padding-top: 24px;
                }
            }
        }

        &:not(:last-child) {
            border-bottom: 1px solid $gray;
        }

        &--hidden {
            display: none;
        }

        &--freeze-open {
            .accordion__header {
                display: none;
            }
        }

        &--error {
            .accordion__content {
                color: $secondary-text-color;
            }

            .accordion__title-text {
                color: $secondary-text-color;
            }

            .accordion__title-wrapper .svg-icon {
                color: $warning-darkest;
            }
        }

        &--success {
            .accordion__title-wrapper .svg-icon {
                color: $blue-dark;
            }
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        padding: 24px 0;

        .svg-icon {
            width: 24px;
            height: 24px;
            transition: transform 0.25s ease-in-out;
        }
    }

    &__icon {
        &--rotated {
            transform: rotate(var(--arrow-rotation));
        }
    }

    &__title-text {
        @include font-size(18px);
        font-weight: 700;
        line-height: 26px;
        margin: 0;
    }

    &__content {
        @include font-size(16px);
        line-height: 24px;
        text-align: left;
        padding-bottom: 24px;
    }

    &__title-wrapper {
        display: flex;

        .svg-icon {
            width: 20px;
            height: 20px;
            flex-grow: 1;
            margin-right: 4px;
        }
    }

    &--cream {
        background-color: $cream-white;
    }

    &--bottom-spacing-small {
        margin-bottom: 16px;
    }

    &--bottom-spacing-medium {
        margin-bottom: 24px;
    }

    &--bottom-spacing-large {
        margin-bottom: 32px;
    }

    &--minimalistic {
        padding: 0;

        .accordion__item {
            border: none;

            &:not(:last-child) {
                margin-bottom: 16px;
            }
        }

        .accordion__header {
            @include font-size(16px);
            padding: 0;
            font-weight: normal;
        }

        .accordion__title-text {
            @include font-size(14px);
            font-weight: normal;
            line-height: normal;
        }

        .accordion__content {
            padding: 4px 0 0 24px;
            @include font-size(12px);
            line-height: 16px;
        }
    }
}
</style>
